import { type ManualJobResolution } from 'primary-schema';

import OrchestratorAPI from './common.js';
import {
  type FindAllOptions,
  type JobStatus,
  type ManualJob,
  type ManualJobWithPayload,
  type ViewManualJob,
} from '../models/orchestrator/manualJob.js';

const manualJobDateFields: (keyof ManualJob)[] = [
  'created',
  'updated',
  'jobCreated',
  'encounterStartTime',
  'claimed',
  'expires',
];

export default class API extends OrchestratorAPI {
  protected readonly API_PATH = '/manual-jobs';
  baseEndpoint = this.API_PATH;

  async findAll(options: FindAllOptions): Promise<ViewManualJob[]> {
    const url = this.constructPath('/search');
    const { jobs } = await this.common.postJSON(url, options);
    return jobs.map((job: ManualJob) => this.deserialize(job, { dateFields: manualJobDateFields }));
  }

  async findOneByJobId(jobId: number): Promise<ManualJobWithPayload> {
    const url = this.constructPath(`/${jobId}`);
    const { job } = await this.common.getJSON(url);
    return this.deserialize(job, { dateFields: manualJobDateFields });
  }

  async create(jobId: number, failureReasons: string[] | null): Promise<ManualJob> {
    const url = this.constructPath(`/${jobId}`, {
      failureReason: failureReasons,
    });
    const { job } = await this.common.putJSON(url);
    return this.deserialize(job, { dateFields: manualJobDateFields });
  }

  async claim(jobId: number): Promise<ManualJob> {
    const url = this.constructPath(`/${jobId}/user`);
    const { job } = await this.common.patchJSON(url);
    return this.deserialize(job, { dateFields: manualJobDateFields });
  }

  async disclaim(jobId: number, userId?: string): Promise<ManualJob> {
    const url = this.constructPath(`/${jobId}/user${userId ? `/${userId}` : ''}`);
    const { job } = await this.common.deleteJSON(url);
    return this.deserialize(job, { dateFields: manualJobDateFields });
  }

  async resolve(jobId: number, resolution?: ManualJobResolution): Promise<void> {
    const url = this.constructPath(`/${jobId}`, { resolution });
    return await this.common.deleteJSON(url);
  }

  async updateManualJob(
    jobId: number,
    updates: { comment?: string; status?: JobStatus }
  ): Promise<ManualJob> {
    const url = this.constructPath(`/${jobId}`);
    const { manualJob } = await this.common.patchJSON(url, updates);
    return this.deserialize(manualJob, { dateFields: manualJobDateFields });
  }
}
